// crewbit-admin/src/components/money/ExpensesTable.tsx
//
// T2125-grouped expenses table for /money/expenses. Composes T2125SectionRow
// (one per craLine bucket) + ExpenseTableRow (one per expense). Section
// subtotals via `groupExpensesByT2125` from `lib/t2125Mapping`.
//
// Sortable column headers deferred (post-launch per spec §Expenses.8 #18).
// Within each section, rows are pre-sorted by date desc by the helper.
//
// Spec: docs/references/MONEY_MODULE_SPEC_v1.md §Expenses.7.2.

import { Fragment } from 'react';
import { palette, admin, spacing } from '../../styles/tokens';
import { groupExpensesByT2125 } from '../../lib/t2125Mapping';
import { T2125SectionRow } from './T2125SectionRow';
import { ExpenseTableRow } from './ExpenseTableRow';
import type { Expense } from '../../types';

interface ExpensesTableProps {
  expenses: Expense[];
  onRowClick: (expense: Expense) => void;
}

const COL_COUNT = 5;

const TH: React.CSSProperties = {
  padding: `${spacing.sm}px ${spacing.md}px`,
  fontSize: 11.5,
  fontWeight: 600,
  color: palette.g500,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  textAlign: 'left',
  borderBottom: `1px solid ${palette.g150}`,
  background: palette.white,
};

export function ExpensesTable({ expenses, onRowClick }: ExpensesTableProps) {
  const sections = groupExpensesByT2125(expenses);

  return (
    <div
      style={{
        background: palette.white,
        border: `1px solid ${admin.border}`,
        borderRadius: admin.cardRadius,
        overflow: 'hidden',
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...TH, width: 130 }}>Date</th>
            <th style={TH}>Vendor</th>
            <th style={TH}>Category</th>
            <th style={TH}>Tags</th>
            <th style={{ ...TH, textAlign: 'right', width: 130 }}>Amount</th>
          </tr>
        </thead>
        <tbody>
          {sections.map((section) => (
            <Fragment key={section.craLine}>
              <T2125SectionRow
                craLine={section.craLine}
                groupLabel={section.groupLabel}
                count={section.expenses.length}
                subtotal={section.subtotal}
                colSpan={COL_COUNT}
              />
              {section.expenses.map((expense) => (
                <ExpenseTableRow
                  key={expense.id}
                  expense={expense}
                  onClick={onRowClick}
                />
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}
